import { useState, useCallback, useEffect } from 'react';
import { UserPlus, Trash2 } from 'lucide-react';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { useAddRoomMembers } from '../hooks/queries/useSpaceQueries';
import { IUser, IRoomMember, ISpaceMember } from '../types';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import UserAutocomplete from './UserAutocomplete';

interface IAddRoomMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  roomName: string;
  spaceId?: string | null;
}

interface IMemberEntry {
  userId: string;
  displayName: string;
  username: string;
  avatar?: string | null;
}

const AVATAR_COLOR = '#3b82f6';

const AddRoomMemberDialog = ({
  isOpen,
  onClose,
  roomId,
  roomName,
  spaceId,
}: IAddRoomMemberDialogProps) => {
  const { t } = useTranslation();
  const [pendingMembers, setPendingMembers] = useState<IMemberEntry[]>([]);
  const [existingMemberIds, setExistingMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const addMembers = useAddRoomMembers();

  useEffect(() => {
    if (!isOpen) return;

    api
      .get<IRoomMember[]>(`/backoffice/rooms/${roomId}/members`)
      .then((members) => {
        setExistingMemberIds(new Set(members.map((m) => m.userId)));
      })
      .catch(() => setExistingMemberIds(new Set()));
  }, [isOpen, roomId]);

  const pendingUserIds = pendingMembers.map((m) => m.userId);

  const handleRemoveUser = (userId: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.userId !== userId));
  };

  const fetchAllUsers = useCallback(
    async (search?: string) => {
      // If room belongs to a space, only show space members
      if (spaceId) {
        const spaceMembers = await api.get<ISpaceMember[]>(
          `/backoffice/spaces/${spaceId}/members`,
        );

        let filtered = spaceMembers.filter(
          (m) =>
            !pendingUserIds.includes(m.userId) &&
            !existingMemberIds.has(m.userId),
        );

        if (search) {
          const q = search.toLowerCase();
          filtered = filtered.filter(
            (m) =>
              m.user?.displayName?.toLowerCase().includes(q) ||
              m.user?.username?.toLowerCase().includes(q),
          );
        }

        return filtered.map((m) => ({
          id: m.userId,
          username: m.user?.username || m.userId,
          displayName: m.user?.displayName || m.user?.username || m.userId,
          avatar: m.user?.avatar,
        }));
      }

      // Fallback: search all users (for rooms not in a space)
      const params = new URLSearchParams();

      if (search) params.append('search', search);
      params.append('limit', '20');

      const result = await api.get<{ data: IUser[] }>(
        `/backoffice/users?${params.toString()}`,
      );

      return (result.data || [])
        .filter(
          (u) => !pendingUserIds.includes(u.id) && !existingMemberIds.has(u.id),
        )
        .map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName || u.username,
          avatar: u.avatar,
        }));
    },
    [pendingUserIds, existingMemberIds, spaceId],
  );

  const handleSelectUser = (userId: string) => {
    if (!userId || pendingUserIds.includes(userId)) return;

    fetchAllUsers().then((users) => {
      const user = users.find((u) => u.id === userId);

      if (user) {
        setPendingMembers((prev) => [
          ...prev,
          {
            userId: user.id,
            displayName: user.displayName,
            username: user.username,
            avatar: user.avatar,
          },
        ]);
      }
    });

    setAutocompleteKey((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (pendingMembers.length === 0) return;
    setError(null);

    addMembers.mutate(
      { roomId, userIds: pendingUserIds },
      {
        onSuccess: () => {
          setPendingMembers([]);
          onClose();
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  const handleClose = () => {
    setPendingMembers([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('chatroom.add_member_title')}</DialogTitle>
          <DialogDescription>
            {t('chatroom.add_member_desc', { name: roomName })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            {t('spaces.select_users')}
          </label>
          <UserAutocomplete
            key={autocompleteKey}
            value=""
            onChange={handleSelectUser}
            fetchUsers={fetchAllUsers}
            placeholder={t('spaces.search_users')}
            triggerPlaceholder={t('spaces.select_user_placeholder')}
          />
        </div>

        <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
          {pendingMembers.length > 0 ? (
            pendingMembers.map((member) => (
              <div
                key={member.userId}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                  style={{ backgroundColor: AVATAR_COLOR }}
                >
                  {member.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {member.displayName}
                  </div>
                  <div className="text-xs text-gray-500">
                    @{member.username}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveUser(member.userId)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400 p-4 text-center">
              {t('spaces.no_members_selected')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={pendingMembers.length === 0 || addMembers.isPending}
          >
            <UserPlus className="w-4 h-4 mr-1" />
            {addMembers.isPending
              ? t('common.saving')
              : t('spaces.confirm_add_members', {
                  count: pendingMembers.length,
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRoomMemberDialog;
